import React, { useEffect, useRef, useState } from 'react';
import { useTour, resumeStepOf } from './tourCore';
import { ROUTES, ROUTE_META } from './tourSteps';

// 操作引导 entry points that live inside other screens:
//   TourCatalogue    — the route catalogue under every workspace tab's 「这是什么？」 (prototype CoachSteps)
//   TourEntryButton  — the permanent 「操作引导」 button in the workspace top bar + its menu (EntryTop)
//   statusLabel      — 「看到第 N 步」 / 「已看完」 / 「有新内容」 / 「未开始」

const C = { brand: '#111111', ink: '#111111', body: '#3A3835', sub: '#6E685F', mute: '#6E685F', line: '#E4DFD7', line2: '#D6D0C7', ok: '#3F7D5A', amber: '#7A4A15' };

export function statusLabel(p) {
  if (!p) return { text: '未开始', color: C.mute };
  if (p.completed && p.outdated) return { text: '有新内容', color: '#9E2B25' };
  if (p.completed) return { text: '已看完', color: C.ok };
  if (Number(p.last_step) > 0) return { text: `看到第 ${p.last_step} 步`, color: C.amber };
  return { text: '未开始', color: C.mute };
}

export const QuestionIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14" /><circle cx="12" cy="17" r=".6" fill="currentColor" />
  </svg>
);

const smallBtn = (primary) => ({
  height: 36, padding: primary ? '0 16px' : '0 14px', borderRadius: 2, font: 'inherit', fontSize: 13.5, cursor: 'pointer',
  border: primary ? 'none' : `1px solid ${C.line2}`, background: primary ? C.brand : '#FFFDF9', color: primary ? '#FFFDF9' : C.ink, fontWeight: primary ? 600 : 400,
});

/** The whole route as a checklist: ✓ seen · current · to do, with 「从第 N 步继续」 / 「从头再看」. */
export function TourCatalogue({ route: routeProp }) {
  const tour = useTour();
  const [route, setRoute] = useState(routeProp || tour.defaultRoute);
  if (!tour.available) return null;
  const steps = ROUTES[route] || [];
  const p = tour.progress[route] || {};
  const total = steps.length;
  const reached = p.completed ? total + 1 : Math.max(0, Number(p.last_step) || 0);
  const seen = p.completed ? total : Math.max(0, reached - 1);
  const resume = resumeStepOf(p, route);
  const other = route === 'coach' ? 'supervisor' : 'coach';
  return (
    <section aria-label="引导目录" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderTop: '1px solid #EFEAE2', paddingTop: 10 }}>
        <b style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
          {ROUTE_META[route].name} · {p.completed ? `已看完 ${total} / ${total} 步` : `已看 ${seen} / ${total} 步`}
          {p.completed && p.outdated ? <span style={{ marginLeft: 8, fontSize: 12, color: '#9E2B25' }}>有新内容</span> : null}
        </b>
        <span style={{ fontSize: 12, color: C.sub }}>约 3 分钟 · 示例会员，不保存数据</span>
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
        {steps.map((s, i) => {
          const no = i + 1;
          const done = no < reached;
          const cur = !p.completed && no === reached;
          return (
            <li key={s.key}>
              <button type="button" onClick={() => tour.start(route, no)} title={`从这一步看起：${s.title}`}
                style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', padding: cur ? '7px 10px' : '7px 0', margin: cur ? '0 -10px' : 0, boxSizing: cur ? 'content-box' : 'border-box',
                  border: 'none', background: cur ? '#EFEAE2' : 'none', borderRadius: 2, font: 'inherit', fontSize: 13.5, textAlign: 'left', cursor: 'pointer', color: done || cur ? C.ink : C.body }}>
                {done ? (
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: C.ok, color: '#FFFDF9', fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>✓</span>
                ) : (
                  <span style={{ width: 20, height: 20, borderRadius: '50%', border: cur ? `2px solid ${C.brand}` : `1px solid ${C.line2}`, color: cur ? C.brand : C.mute, fontWeight: cur ? 700 : 400, fontSize: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', flex: '0 0 auto' }}>{no}</span>
                )}
                {cur ? <b style={{ fontWeight: 600 }}>{s.cat}</b> : <span>{s.cat}</span>}
                {cur && <span style={{ marginLeft: 'auto', fontSize: 12, color: C.brand, whiteSpace: 'nowrap' }}>上次看到这里</span>}
              </button>
            </li>
          );
        })}
      </ol>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid #EFEAE2', paddingTop: 12 }}>
        <button type="button" onClick={() => setRoute(other)} style={{ marginRight: 'auto', border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: 12.5, color: C.brand, cursor: 'pointer' }}>
          看{ROUTE_META[other].name} →
        </button>
        <button type="button" onClick={() => tour.start(route, 1)} style={smallBtn(false)}>从头再看</button>
        {!p.completed && reached > 1 && <button type="button" onClick={() => tour.start(route, resume)} style={smallBtn(true)}>从第 {resume} 步继续</button>}
        {!p.completed && reached <= 1 && <button type="button" onClick={() => tour.start(route, 1)} style={smallBtn(true)}>开始（3 分钟）</button>}
      </div>
    </section>
  );
}

/** 「操作引导」 in the workspace top bar (red dot while unfinished) + the EntryTop menu. */
export function TourEntryButton({ compact = false }) {
  const tour = useTour();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  if (!tour.available) return null;

  const card = (route) => {
    const p = tour.progress[route] || {};
    const total = ROUTES[route].length;
    const st = statusLabel(p);
    const going = !p.completed && Number(p.last_step) > 0;
    const resume = resumeStepOf(p, route);
    const hot = route === tour.defaultRoute;
    return (
      <button key={route} type="button" onClick={() => { setOpen(false); tour.start(route, resume); }}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px', borderRadius: 2, textAlign: 'left', font: 'inherit', cursor: 'pointer', color: C.ink,
          background: hot ? '#EFEAE2' : '#FFFDF9', border: `1px solid ${hot ? '#E4DFD7' : C.line}` }}>
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <b style={{ fontSize: 14.5, fontWeight: 700, color: hot ? C.brand : C.ink }}>{ROUTE_META[route].name} · {total} 步</b>
          <span style={{ fontSize: 12, color: st.color, fontWeight: 600 }}>{st.text}</span>
        </span>
        {going && (
          <span style={{ height: 5, borderRadius: 3, background: '#E4DFD7', overflow: 'hidden', display: 'block' }}>
            <span style={{ display: 'block', width: `${Math.round((p.last_step / total) * 100)}%`, height: '100%', background: C.brand }} />
          </span>
        )}
        <span style={{ fontSize: 12.5, color: C.body, lineHeight: 1.55 }}>
          {going ? `点这里从第 ${resume} 步「${ROUTES[route][resume - 1].cat}」继续` : (ROUTE_META[route].shortBlurb || ROUTE_META[route].blurb)}
        </span>
      </button>
    );
  };

  return (
    <div ref={ref} style={{ position: 'relative', flex: '0 0 auto' }}>
      <button type="button" aria-expanded={open} aria-label="操作引导" title="操作引导" onClick={() => setOpen((v) => !v)}
        style={{ position: 'relative', height: 34, padding: compact ? '0 9px' : '0 12px', borderRadius: 2, border: `1px solid ${open ? C.brand : C.line}`, background: open ? '#EFEAE2' : '#FFFDF9',
          color: C.brand, font: 'inherit', fontSize: 13.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <QuestionIcon />{compact ? null : '操作引导'}
        {tour.unfinished && <span aria-label="还没看完" style={{ position: 'absolute', top: -3, right: -3, width: 9, height: 9, borderRadius: '50%', background: '#9E2B25', border: '2px solid #FFFDF9' }} />}
      </button>
      {open && (
        <section aria-label="操作引导菜单" style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 40, width: 'min(380px, calc(100vw - 24px))', background: '#FFFDF9',
          border: `1px solid ${C.line}`, borderRadius: 2, boxShadow: 'none', padding: 14, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 12.5, color: C.sub, padding: '0 4px 4px' }}>用示例会员走一遍，不会保存任何数据</div>
          {card('coach')}
          {card('supervisor')}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 4px 0', borderTop: '1px solid #EFEAE2', marginTop: 2 }}>
            <button type="button" onClick={() => { setOpen(false); tour.openIntro(); }} style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: 13, fontWeight: 500, color: C.brand, cursor: 'pointer' }}>只看「这个页签怎么用」</button>
            <button type="button" onClick={() => { setOpen(false); tour.start(tour.defaultRoute, 1); }} style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontSize: 13, color: C.sub, cursor: 'pointer' }}>从头再看</button>
          </div>
        </section>
      )}
    </div>
  );
}
